import { useSyncExternalStore } from 'react';
import type { Track } from '@blackblack/shared';
import { api } from '../lib/api';

/**
 * Global music engine. Lives outside React (one <audio> element for the whole
 * app) so playback continues across navigation; components subscribe to the
 * slices they need. Rights-limited tracks are played as their clip window only.
 */
export type RepeatMode = 'off' | 'all' | 'one';

export interface PlayerState {
  queue: Track[];
  index: number;
  playing: boolean;
  buffering: boolean;
  /** Seconds, relative to the playable window (clip-aware). */
  position: number;
  duration: number;
  shuffle: boolean;
  repeat: RepeatMode;
  volume: number;
  expanded: boolean;
  queueOpen: boolean;
  context: string | null;
  error: string | null;
}

type Listener = () => void;

class PlayerEngine {
  private audio = new Audio();
  private listeners = new Set<Listener>();
  private unshuffled: Track[] | null = null;
  private countedPlay = false;
  state: PlayerState = {
    queue: [],
    index: -1,
    playing: false,
    buffering: false,
    position: 0,
    duration: 0,
    shuffle: false,
    repeat: 'off',
    volume: 1,
    expanded: false,
    queueOpen: false,
    context: null,
    error: null,
  };

  constructor() {
    const a = this.audio;
    a.preload = 'auto';
    a.addEventListener('timeupdate', () => this.onTime());
    a.addEventListener('playing', () => this.set({ playing: true, buffering: false, error: null }));
    a.addEventListener('pause', () => this.set({ playing: false }));
    a.addEventListener('waiting', () => this.set({ buffering: true }));
    a.addEventListener('ended', () => this.onEnded());
    a.addEventListener('loadedmetadata', () => {
      const w = this.window();
      if (w && a.currentTime < w.start) a.currentTime = w.start;
      // Streams without a known length report Infinity; fall back to catalogue metadata.
      this.set({ duration: w ? w.length : Number.isFinite(a.duration) && a.duration > 0 ? a.duration : this.current?.durationSec ?? 0 });
    });
    a.addEventListener('error', () => this.set({ playing: false, buffering: false, error: 'Dit nummer kan nu niet worden afgespeeld' }));
    this.bindMediaSession();
  }

  /* ---------- store plumbing ---------- */

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private set(patch: Partial<PlayerState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  get current(): Track | null {
    return this.state.queue[this.state.index] ?? null;
  }

  /** Playable window for rights-limited tracks. */
  private window(t: Track | null = this.current): { start: number; length: number } | null {
    if (!t || t.fullPlayback || !t.clip) return null;
    return { start: t.clip.startSec, length: t.clip.durationSec };
  }

  /* ---------- commands ---------- */

  playTracks(tracks: Track[], startIndex = 0, context: string | null = null) {
    const playable = tracks.filter((t) => t.streamUrl);
    if (!playable.length) {
      this.set({ error: 'Geen afspeelbare nummers' });
      return;
    }
    const start = Math.max(0, playable.findIndex((t) => t.id === tracks[startIndex]?.id));
    this.unshuffled = null;
    let queue = playable;
    let index = start;
    if (this.state.shuffle) {
      this.unshuffled = playable;
      const first = playable[start]!;
      queue = [first, ...shuffle(playable.filter((_, i) => i !== start))];
      index = 0;
    }
    this.set({ queue, index, context });
    this.load(true);
  }

  playTrack(track: Track, context: string | null = null) {
    const i = this.state.queue.findIndex((t) => t.id === track.id);
    if (i !== -1 && i === this.state.index) return this.toggle();
    this.playTracks([track], 0, context);
  }

  toggle() {
    if (!this.current) return;
    if (this.audio.paused) this.resume();
    else this.audio.pause();
  }

  pause() {
    this.audio.pause();
  }

  private resume() {
    window.dispatchEvent(new CustomEvent('bb:music-play'));
    void this.audio.play().catch(() => this.set({ playing: false }));
  }

  next(auto = false) {
    const { queue, index, repeat } = this.state;
    if (!queue.length) return;
    if (auto && repeat === 'one') return this.seek(0, true);
    if (index < queue.length - 1) this.set({ index: index + 1 });
    else if (repeat === 'all' || !auto) this.set({ index: 0 });
    else {
      this.audio.pause();
      this.seek(0);
      return;
    }
    this.load(true);
  }

  prev() {
    if (this.state.position > 3 || this.state.index === 0) return this.seek(0, true);
    this.set({ index: this.state.index - 1 });
    this.load(true);
  }

  seek(sec: number, play = false) {
    const w = this.window();
    const clamped = Math.max(0, Math.min(sec, this.state.duration || sec));
    this.audio.currentTime = (w?.start ?? 0) + clamped;
    this.set({ position: clamped });
    if (play) this.resume();
  }

  jumpTo(index: number) {
    if (index < 0 || index >= this.state.queue.length) return;
    this.set({ index });
    this.load(true);
  }

  addToQueue(track: Track, mode: 'next' | 'end' = 'end') {
    if (!track.streamUrl) return;
    if (!this.current) return this.playTracks([track]);
    const q = [...this.state.queue];
    q.splice(mode === 'next' ? this.state.index + 1 : q.length, 0, track);
    this.set({ queue: q });
  }

  removeFromQueue(i: number) {
    if (i === this.state.index) return;
    const q = this.state.queue.filter((_, k) => k !== i);
    this.set({ queue: q, index: i < this.state.index ? this.state.index - 1 : this.state.index });
  }

  toggleShuffle() {
    const { shuffle: on, queue, index } = this.state;
    const cur = queue[index];
    if (!on) {
      this.unshuffled = queue;
      const rest = shuffle(queue.filter((_, i) => i !== index));
      this.set({ shuffle: true, queue: cur ? [cur, ...rest] : rest, index: cur ? 0 : -1 });
    } else {
      const original = this.unshuffled ?? queue;
      this.unshuffled = null;
      this.set({ shuffle: false, queue: original, index: cur ? original.findIndex((t) => t.id === cur.id) : -1 });
    }
  }

  cycleRepeat() {
    const order: RepeatMode[] = ['off', 'all', 'one'];
    this.set({ repeat: order[(order.indexOf(this.state.repeat) + 1) % 3]! });
  }

  setVolume(v: number) {
    this.audio.volume = Math.max(0, Math.min(1, v));
    this.set({ volume: this.audio.volume });
  }

  setExpanded(expanded: boolean) {
    this.set({ expanded, queueOpen: expanded ? this.state.queueOpen : false });
  }

  setQueueOpen(queueOpen: boolean) {
    this.set({ queueOpen });
  }

  /** Updates track data in the queue (e.g. after liking). */
  patchTrack(id: string, patch: Partial<Track>) {
    if (!this.state.queue.some((t) => t.id === id)) return;
    this.set({ queue: this.state.queue.map((t) => (t.id === id ? { ...t, ...patch } : t)) });
  }

  stop() {
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.set({ queue: [], index: -1, playing: false, position: 0, duration: 0, expanded: false, context: null });
  }

  /* ---------- internals ---------- */

  private load(autoplay: boolean) {
    const t = this.current;
    if (!t?.streamUrl) return this.next(true);
    this.countedPlay = false;
    this.audio.src = t.streamUrl;
    const w = this.window(t);
    this.set({ position: 0, duration: w ? w.length : t.durationSec, buffering: true, error: null });
    if (w) this.audio.currentTime = w.start;
    if (autoplay) this.resume();
    this.updateMediaSession();
  }

  private onTime() {
    const w = this.window();
    const pos = this.audio.currentTime - (w?.start ?? 0);
    if (w && pos >= w.length) return this.onEnded();
    if (Math.abs(pos - this.state.position) >= 0.25) this.set({ position: Math.max(0, pos) });
    // Count a play after 10s (or half of a short clip) — mirrors common industry practice.
    const threshold = Math.min(10, (this.state.duration || 20) / 2);
    if (!this.countedPlay && pos >= threshold && this.current) {
      this.countedPlay = true;
      void api.post(`/music/tracks/${this.current.id}/play`, { context: { source: this.state.context ?? 'player' } }).catch(() => {});
    }
  }

  private onEnded() {
    this.next(true);
  }

  private bindMediaSession() {
    const ms = navigator.mediaSession;
    if (!ms) return;
    ms.setActionHandler('play', () => this.resume());
    ms.setActionHandler('pause', () => this.pause());
    ms.setActionHandler('nexttrack', () => this.next());
    ms.setActionHandler('previoustrack', () => this.prev());
    ms.setActionHandler('seekto', (d) => d.seekTime !== undefined && this.seek(d.seekTime));
  }

  private updateMediaSession() {
    const t = this.current;
    if (!t || !navigator.mediaSession || typeof MediaMetadata === 'undefined') return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: t.title,
      artist: t.artist.name,
      album: t.album?.title ?? 'BlackBlack',
      artwork: t.coverUrl ? [{ src: t.coverUrl, sizes: '512x512' }] : [],
    });
  }
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export const player = new PlayerEngine();

export function usePlayer<T>(select: (s: PlayerState) => T): T {
  return useSyncExternalStore(player.subscribe, () => select(player.state));
}

export function useCurrentTrack(): Track | null {
  return usePlayer((s) => s.queue[s.index] ?? null);
}
